/**
 * Comparaison de deux versions d'un prompt, ligne par ligne (cahier des
 * charges §7.7) : lignes communes, retirées, ajoutées.
 */
export interface LigneDiff {
  type: 'egale' | 'retiree' | 'ajoutee';
  texte: string;
  /** Numéro de ligne dans l'ancienne version (égale ou retirée). */
  avant?: number;
  /** Numéro de ligne dans la nouvelle version (égale ou ajoutée). */
  apres?: number;
}

/** Au-delà, la partie centrale n'est pas alignée finement (tout retiré puis tout ajouté). */
const CELLULES_MAX = 4_000_000;

export function diffLignes(ancien: string, nouveau: string): LigneDiff[] {
  const a = ancien.replace(/\r\n/g, '\n').split('\n');
  const b = nouveau.replace(/\r\n/g, '\n').split('\n');

  // Début et fin communs : l'essentiel d'une retouche de prompt.
  let debut = 0;
  while (debut < a.length && debut < b.length && a[debut] === b[debut]) debut++;
  let fin = 0;
  while (fin < a.length - debut && fin < b.length - debut && a[a.length - 1 - fin] === b[b.length - 1 - fin]) fin++;

  const milieuA = a.slice(debut, a.length - fin);
  const milieuB = b.slice(debut, b.length - fin);
  const lignes: LigneDiff[] = [];
  for (let i = 0; i < debut; i++) lignes.push({ type: 'egale', texte: a[i]!, avant: i + 1, apres: i + 1 });

  const n = milieuA.length;
  const m = milieuB.length;
  if (n * m > CELLULES_MAX) {
    milieuA.forEach((t, i) => lignes.push({ type: 'retiree', texte: t, avant: debut + i + 1 }));
    milieuB.forEach((t, j) => lignes.push({ type: 'ajoutee', texte: t, apres: debut + j + 1 }));
  } else {
    // Plus longue sous-suite commune, calculée depuis la fin.
    const lcs = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
    for (let i = n - 1; i >= 0; i--) {
      for (let j = m - 1; j >= 0; j--) {
        lcs[i]![j] = milieuA[i] === milieuB[j] ? lcs[i + 1]![j + 1]! + 1 : Math.max(lcs[i + 1]![j]!, lcs[i]![j + 1]!);
      }
    }
    let i = 0;
    let j = 0;
    while (i < n || j < m) {
      if (i < n && j < m && milieuA[i] === milieuB[j]) {
        lignes.push({ type: 'egale', texte: milieuA[i]!, avant: debut + i + 1, apres: debut + j + 1 });
        i++;
        j++;
      } else if (i < n && (j === m || lcs[i + 1]![j]! >= lcs[i]![j + 1]!)) {
        // À égalité, la ligne retirée d'abord : l'ancien texte se lit avant le nouveau.
        lignes.push({ type: 'retiree', texte: milieuA[i]!, avant: debut + i + 1 });
        i++;
      } else {
        lignes.push({ type: 'ajoutee', texte: milieuB[j]!, apres: debut + j + 1 });
        j++;
      }
    }
  }

  for (let k = fin; k > 0; k--) {
    lignes.push({ type: 'egale', texte: a[a.length - k]!, avant: a.length - k + 1, apres: b.length - k + 1 });
  }
  return lignes;
}

export interface BlocDiff {
  /** Lignes affichées (changements et contexte autour). */
  lignes: LigneDiff[];
  /** Lignes communes masquées avant ce bloc. */
  masquees: number;
}

/**
 * Regroupe les changements avec `contexte` lignes communes autour ; les
 * longues parties identiques sont repliées.
 */
export function blocsDiff(lignes: LigneDiff[], contexte = 3): { blocs: BlocDiff[]; masqueesFin: number } {
  const garder = lignes.map(() => false);
  lignes.forEach((l, i) => {
    if (l.type === 'egale') return;
    for (let k = Math.max(0, i - contexte); k <= Math.min(lignes.length - 1, i + contexte); k++) garder[k] = true;
  });
  const blocs: BlocDiff[] = [];
  let masquees = 0;
  let courant: BlocDiff | null = null;
  lignes.forEach((l, i) => {
    if (!garder[i]) {
      masquees++;
      courant = null;
      return;
    }
    if (!courant) {
      courant = { lignes: [], masquees };
      blocs.push(courant);
      masquees = 0;
    }
    courant.lignes.push(l);
  });
  return { blocs, masqueesFin: masquees };
}

export function resumeDiff(lignes: LigneDiff[]): { ajoutees: number; retirees: number } {
  return {
    ajoutees: lignes.filter((l) => l.type === 'ajoutee').length,
    retirees: lignes.filter((l) => l.type === 'retiree').length,
  };
}
